import { create, enforce, test } from 'vest';

import { ContactDetailsValues } from '../../state/types';
import { isBlank } from '../../utils/isBlank';

import { CANADA } from './consts';

export const isEmail = (value: string) => {
  if (isBlank(value)) {
    return true;
  }
  if (value.length > 255) {
    return false;
  }
  const emailRegex =
    // eslint-disable-next-line no-control-regex, no-useless-escape
    /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
  return emailRegex.test(value);
};

export const isPhoneNumber = (value: string) => {
  if (isBlank(value)) {
    return false;
  }
  const phoneRegex =
    /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;
  return phoneRegex.test(value);
};

export const usZipCodeRegExp = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
export const caZipCodeRegExp = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

const usOrcaZipCodeRegExp = new RegExp(`(${usZipCodeRegExp.source})|(${caZipCodeRegExp.source})`, 'i');

const CHARACTER_LIMIT = 100;
const EMAIL_CHARACTER_LIMIT = 100;
const ADDRESS_LINE_1_CHARACTER_LIMIT = 100;
const ADDRESS_LINE_2_CHARACTER_LIMIT = 50;
const CITY_CHARACTER_LIMIT = 50;

export const zipCodeSuite = create((value: string) => {
  test('zipCode', () => {
    enforce(value).message(`Zip code is required`).isNotEmpty();
    enforce(value)
      .message(`Zip code is not valid`)
      .condition(() => usOrcaZipCodeRegExp.test(value));
  });
});

const suite = create((values: ContactDetailsValues) => {
  test('firstName', () => {
    enforce(values.firstName).message('First name is required').isNotEmpty();
    enforce(values.firstName.length).message(`Max character limit: ${CHARACTER_LIMIT}`).lessThan(CHARACTER_LIMIT);
  });
  test('lastName', () => {
    enforce(values.lastName).message('Last name is required').isNotEmpty();
    enforce(values.lastName.length).message(`Max character limit: ${CHARACTER_LIMIT}`).lessThan(CHARACTER_LIMIT);
  });

  test('phone', () => {
    enforce(values.phone).message('Phone number is required').isNotEmpty();
    enforce(values.phone)
      .message('Phone number is not valid')
      .condition((value) => isPhoneNumber(value));
  });

  test('email', () => {
    enforce(values.email).message('E-mail address is required').isNotEmpty();
    enforce(values.email.length)
      .message(`Max character limit: ${EMAIL_CHARACTER_LIMIT}`)
      .lessThan(EMAIL_CHARACTER_LIMIT);
    enforce(values.email)
      .message('E-mail address is not valid')
      .condition((value) => {
        return isEmail(value);
      });
  });

  test('country', () => {
    enforce(values.country).message('Country is required').isNotEmpty();
  });

  const isCanada = values.country === CANADA;

  test('addressLine1', () => {
    enforce(values.addressLine1).message('Street address is required').isNotEmpty();
    enforce(values.addressLine1.length)
      .message(`Max character limit: ${ADDRESS_LINE_1_CHARACTER_LIMIT}`)
      .lessThan(ADDRESS_LINE_1_CHARACTER_LIMIT);
  });

  test('addressLine2', () => {
    enforce(values.addressLine2.length)
      .message(`Max character limit: ${ADDRESS_LINE_2_CHARACTER_LIMIT}`)
      .lessThan(ADDRESS_LINE_2_CHARACTER_LIMIT);
  });

  test('state', () => {
    enforce(values.state)
      .message(`${isCanada ? 'Province' : 'State'} is required`)
      .isNotEmpty();
    enforce(values.state)
      .message(`Please use ${isCanada ? 'province' : 'state'} abbreviation`)
      .lengthEquals(2);
  });

  test('city', () => {
    enforce(values.city).message('City is required').isNotEmpty();
    enforce(values.city.length).message(`Max character limit: ${CITY_CHARACTER_LIMIT}`).lessThan(CITY_CHARACTER_LIMIT);
  });

  test('zipCode', () => {
    enforce(values.zipCode)
      .message(`${isCanada ? 'Postal' : 'Zip'} code is required`)
      .isNotEmpty();
    enforce(values.zipCode)
      .message(`${isCanada ? 'Postal' : 'Zip'} code is not valid`)
      .condition((value) => (values.country === 'Canada' ? caZipCodeRegExp : usZipCodeRegExp).test(value));
  });
});

export const onValidate = (isCustomerPortal: boolean) => (values: ContactDetailsValues) => {
  const { getErrors } = suite(values);
  const errors = getErrors();
  if (isCustomerPortal) {
    errors.phone = [];
  }
  return errors;
};
